import React, { memo } from 'react';
import { StringOrNull } from 'store/entity/types';
import { IPrintPartProps } from 'store/entity/IPrintTemplatePart';
import {
  PrintSingleTaskConsumer,
  IPrintSingleTaskContext,
} from 'store/context/PrintSingleTaskContext';
import messages from './Messages';
import SignaturesBox from './SignaturesBox';
import { SignaturesTable } from './styles';
import { ITask } from 'store/tasks/tasksType';
import { ICompanyRole } from 'store/companies/companiesTypes';

interface IUsersSignaturesObject {
  guest: IUserSignatureInfo[];
  absent: IUserSignatureInfo[];
  present: IUserSignatureInfo[];
  vice: IUserSignatureInfo[];
}

export interface IUserSignatureInfo {
  name: string;
  role: string;
  order: number;
  guest: boolean;
  signature: {
    url: StringOrNull;
    status: 'signed' | 'notSigned';
  };
}

export type UserSignaturesType = 'guest' | 'absent' | 'present' | 'vice';

const SignaturePart: React.FC<IPrintPartProps<'signatures'>> = () => (
  <PrintSingleTaskConsumer>
    {(dataContext) => {
      const { members, company, allTasks, taskIds } =
        dataContext as Required<IPrintSingleTaskContext>;

      let parentTask = allTasks.find(
        (task) => task.id === taskIds.parentTask
      ) as ITask;

      const selfTask = allTasks.find(
        (task) => task.id === taskIds.selfTask
      ) as ITask;
      if (!parentTask) parentTask = selfTask;

      const projectMembers = company.projects?.find(
        (it) => it.id === selfTask.projectId
      )?.projectMembers;

      const userSignatures = Object.values(parentTask.assignee).reduce(
        (tmp: IUsersSignaturesObject, { userId, progress, properties }) => {
          const projectMember = Object.values(projectMembers || {})!.find(
            (pm) => {
              return (
                (pm.userId && pm.userId === userId) ||
                (pm.roleId && pm.roleId === properties?.roleId)
              );
            }
          );
          const isProjectMember = properties
            ? properties.implicit !== true
            : projectMember && projectMember?.implicit !== true;

          const userStatus: {
            [status in UserSignaturesType]: IUserSignatureInfo[];
          } = {
            present: tmp.present,
            vice: tmp.vice,
            absent: tmp.absent,
            guest: tmp.guest,
          };

          const isPresent = progress === 100;
          const isVice = progress < 100 && progress > 0;
          const selfAssignee = Object.values(selfTask.assignee).find(
            (a) => a.userId === userId
          );
          const isSign = selfAssignee ? selfAssignee.progress === 100 : null;
          const name = members.find((m) => m.id === userId)!.name;

          const userRole = company.roles?.reduce(
            (current, item) => {
              const finalItem: ICompanyRole = { ...item };
              if (item.roleType === 'District')
                finalItem.order = (finalItem.order || finalItem.id) + 5000;
              if (properties?.roleId && properties?.roleId === current?.id) {
                return current;
              }
              if (properties?.roleId && properties?.roleId === item?.id) {
                return finalItem;
              }
              if (properties?.roleName) {
                return {
                  name: properties.roleName,
                  order: 10000,
                } as ICompanyRole;
              }
              if (
                projectMember?.relatedRoleId &&
                projectMember?.relatedRoleId === current?.id
              )
                return current;
              if (
                item.users &&
                item.users.find((user) => user.userId === userId) &&
                (!projectMember?.relatedRoleId ||
                  projectMember?.relatedRoleId === item.id)
              ) {
                if (!current) {
                  return finalItem;
                }
                if ((current.order || 10000) < (finalItem.order || 10000))
                  return current;
                return finalItem;
              }
              return current;
            },
            null as ICompanyRole | null
          );

          const memberTitle = properties?.title ?? projectMember?.title;
          const finalRoleTitle =
            userRole && memberTitle
              ? `${userRole.name}(${memberTitle})`
              : memberTitle || userRole?.name || messages.noRoleForUser;

          const userSignature = company.members?.find(
            (cm) => cm.userId === userId
          )!.signature;

          const whichStatus: UserSignaturesType = isPresent // isProjectMember
            ? 'present'
            : isVice
              ? 'vice'
              : 'absent';
          // : 'guest';

          userStatus[whichStatus].push({
            name,
            role: finalRoleTitle,
            signature: userSignature
              ? {
                  url: userSignature.url,
                  status: isSign ? 'signed' : 'notSigned',
                }
              : {
                  url: null,
                  status: isSign ? 'signed' : 'notSigned',
                },
            order: userRole?.order || 10000,
            guest: !isProjectMember,
          });

          return { ...userStatus };
        },
        { present: [], vice: [], absent: [], guest: [] }
      );

      return Object.keys(userSignatures).map((whichSignature) => {
        const signatures = userSignatures[whichSignature as UserSignaturesType];
        return (
          signatures.length > 0 && (
            <SignaturesTable key={`Table@${whichSignature}`}>
              <thead>
                <tr>
                  <th colSpan={5}>
                    {messages.getSignatureBoxTitle(
                      whichSignature as UserSignaturesType,
                      undefined,
                      parentTask
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                <SignaturesBox
                  key={whichSignature}
                  title={whichSignature as UserSignaturesType}
                  signatures={signatures}
                />
              </tbody>
            </SignaturesTable>
          )
        );
      });
    }}
  </PrintSingleTaskConsumer>
);

export default memo(SignaturePart);
